import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateOperationalSite } from 'features/operationalManagement/operationalManagementSlice'
import {
  IOperationalSite,
  SiteStatus,
} from 'features/operationalManagement/operationalManagement.types'
import { AppDispatch, RootState } from 'store'
import { Input, Textarea } from 'components/Form/Input'
import { Select, Option } from 'components/Select/Select'
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from 'components/ModalV2/Modal'
import { Button, Flex } from 'styling/GlobalStyles'
import { Heading2, NormalText } from 'styling/TypographyV2'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { ModalFieldHelp } from '../Organogram.styled'

interface EditSiteModalProps {
  site: IOperationalSite
  onClose: () => void
  onUpdated?: () => void
}

const STATUS_OPTIONS: Option[] = [
  { label: 'Active', value: SiteStatus.Active },
  { label: 'Inactive', value: SiteStatus.Inactive },
]

export const EditSiteModal = ({ site, onClose, onUpdated }: EditSiteModalProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const updatingSite = useSelector(
    (state: RootState) => state.operationalManagement.updatingSite,
  )
  const [name, setName] = useState(site.name)
  const [status, setStatus] = useState<SiteStatus>(site.status)
  const [reason, setReason] = useState('')

  const trimmedName = name.trim()
  const trimmedReason = reason.trim()
  const canSave = !!trimmedName && !!trimmedReason && !updatingSite

  const handleSave = async () => {
    if (!canSave) return
    const result = await dispatch(
      updateOperationalSite({
        id: site.id,
        name: trimmedName,
        status,
        reason: trimmedReason,
      }),
    )
    if (updateOperationalSite.fulfilled.match(result)) {
      onUpdated?.()
      onClose()
    }
  }

  const selectedStatus =
    STATUS_OPTIONS.find((o) => o.value === status) ?? STATUS_OPTIONS[0]

  return (
    <Modal onClose={onClose}>
      <ModalContent maxWidth='480px'>
        <ModalHeader>
          <Flex between center>
            <Heading2>Edit site</Heading2>
            <CloseIcon onClick={onClose} />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <NormalText mb='1.25rem'>Update site name and operational status.</NormalText>
          <Flex column gap='1rem'>
            <Input
              label='Site name'
              placeholder='Enter here...'
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            <Select
              label='Status'
              options={STATUS_OPTIONS}
              value={selectedStatus}
              onChange={(opt) => setStatus(opt.value as SiteStatus)}
            />
            <div>
              <Textarea
                label='Reason for change *'
                placeholder='Describe the reason for the change...'
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <ModalFieldHelp>
                Required – all changes are logged in the Audit trail.
              </ModalFieldHelp>
            </div>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button white onClick={onClose} disabled={updatingSite}>
            Cancel
          </Button>
          <Button orange onClick={handleSave} disabled={!canSave}>
            {updatingSite ? 'Saving…' : 'Save changes'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
